import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { BarChart3, TrendingUp, Users, DollarSign, Package, Calendar, Download } from 'lucide-react';

const AnalyticsDashboard = ({ facilityId }) => {
  const { getToken } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30'); // days

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    try {
      const token = await getToken();
      
      const response = await fetch(`${API_URL}/api/partner/analytics/${facilityId}?days=${dateRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      setAnalytics(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setLoading(false);
    }
  };

  const exportReport = async () => {
    try {
      const token = await getToken();
      
      const response = await fetch(`${API_URL}/api/partner/analytics/${facilityId}/export?days=${dateRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  if (loading) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center'
      }}>
        <p style={{ color: '#6b7280' }}>Loading analytics...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
              Analytics & Reports
            </h2>
            <p style={{ color: '#6b7280', fontSize: '14px' }}>
              Track your facility's performance and bookings
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              style={{
                padding: '10px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            
            <button
              onClick={exportReport}
              style={{
                background: '#10b981',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '20px',
        marginBottom: '24px'
      }}>
        <MetricCard
          icon={<Users size={24} color="#667eea" />}
          title="Total Bookings"
          value={analytics?.total_bookings || 0}
          change={analytics?.bookings_change || 0}
          color="#667eea"
        />
        <MetricCard
          icon={<DollarSign size={24} color="#10b981" />}
          title="Total Revenue"
          value={`$${analytics?.total_revenue?.toLocaleString() || 0}`}
          change={analytics?.revenue_change || 0}
          color="#10b981"
        />
        <MetricCard
          icon={<Package size={24} color="#f59e0b" />}
          title="Occupancy Rate"
          value={`${analytics?.occupancy_rate || 0}%`}
          change={analytics?.occupancy_change || 0}
          color="#f59e0b"
        />
        <MetricCard
          icon={<TrendingUp size={24} color="#3b82f6" />}
          title="Avg. Booking Value"
          value={`$${analytics?.avg_booking_value || 0}`}
          change={analytics?.avg_value_change || 0}
          color="#3b82f6"
        />
      </div>

      {/* Charts Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: '20px',
        marginBottom: '24px'
      }}>
        {/* Bookings Over Time */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px' }}>
            Bookings Over Time
          </h3>
          <SimpleLineChart data={analytics?.bookings_timeline || []} />
        </div>

        {/* Storage Type Distribution */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px' }}>
            Storage Type Mix
          </h3>
          <StorageTypePie data={analytics?.storage_type_distribution || []} />
        </div>
      </div>

      {/* Recent Bookings Table */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px' }}>
          Recent Bookings
        </h3>
        <RecentBookingsTable bookings={analytics?.recent_bookings || []} />
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ icon, title, value, change, color }) => (
  <div style={{
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
      <div style={{
        width: '48px',
        height: '48px',
        background: `${color}15`,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {icon}
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '14px',
        fontWeight: '600',
        color: change >= 0 ? '#10b981' : '#ef4444'
      }}>
        <TrendingUp size={16} style={{ transform: change < 0 ? 'rotate(180deg)' : 'none' }} />
        {Math.abs(change)}%
      </div>
    </div>
    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
      {title}
    </div>
    <div style={{ fontSize: '28px', fontWeight: '800', color: '#1a1a1a' }}>
      {value}
    </div>
  </div>
);

// Simple Line Chart (using CSS bars)
const SimpleLineChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <p style={{ color: '#9ca3af', textAlign: 'center' }}>No data available</p>;
  }

  const maxValue = Math.max(...data.map(d => d.value));

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', height: '200px' }}>
      {data.map((item, idx) => (
        <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div
            style={{
              width: '100%',
              height: `${(item.value / maxValue) * 180}px`,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '4px 4px 0 0',
              transition: 'height 0.3s',
              minHeight: '4px'
            }}
            title={`${item.value} bookings`}
          />
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
            {item.label}
          </div>
        </div>
      ))}
    </div>
  );
};

// Storage Type Pie (simple bar representation)
const StorageTypePie = ({ data }) => {
  if (!data || data.length === 0) {
    return <p style={{ color: '#9ca3af', textAlign: 'center' }}>No data available</p>;
  }

  const colors = ['#667eea', '#10b981', '#f59e0b', '#3b82f6', '#ef4444'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {data.map((item, idx) => (
        <div key={idx}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '6px',
            fontSize: '13px'
          }}>
            <span style={{ fontWeight: '600', color: '#374151' }}>{item.type}</span>
            <span style={{ color: '#6b7280' }}>{item.percentage}%</span>
          </div>
          <div style={{
            width: '100%',
            height: '8px',
            background: '#f3f4f6',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${item.percentage}%`,
              height: '100%',
              background: colors[idx % colors.length],
              borderRadius: '4px'
            }} />
          </div>
        </div>
      ))}
    </div>
  );
};

// Recent Bookings Table
const RecentBookingsTable = ({ bookings }) => {
  if (!bookings || bookings.length === 0) {
    return <p style={{ color: '#9ca3af', textAlign: 'center' }}>No recent bookings</p>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Date
            </th>
            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Customer
            </th>
            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Unit
            </th>
            <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Duration
            </th>
            <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '700', color: '#374151' }}>
              Value
            </th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((booking, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '12px', fontSize: '14px' }}>
                {new Date(booking.date).toLocaleDateString()}
              </td>
              <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600' }}>
                {booking.customer_name}
              </td>
              <td style={{ padding: '12px', fontSize: '14px' }}>
                {booking.unit_number}
              </td>
              <td style={{ padding: '12px', fontSize: '14px' }}>
                {booking.duration_months} months
              </td>
              <td style={{ padding: '12px', fontSize: '14px', fontWeight: '700', color: '#10b981', textAlign: 'right' }}>
                ${booking.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AnalyticsDashboard;
